/**
 * 自動応答（アドレス横断一覧）。
 *
 * 個々の編集はメールアドレス詳細の「自動応答」タブが本体で、この画面は
 * 「どのアドレスで自動応答が動いているか」を一望するための入口にする。
 * 長期休暇のあと切り忘れている、という事故に気づけるようにするのが目的。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  CursorPagination,
  DataTable,
  EmptyState,
  PageHeader,
  useCursorPagination,
  type DataTableColumn,
} from '@mail-sv/ui';
import { Reply } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { HelpNote } from '../components/help.js';
import type { CursorPage, Mailbox } from '../lib/api-types.js';

export function AutoresponderPage() {
  const page = useCursorPagination(50);

  const list = useQuery({
    queryKey: qk.mailboxes({ view: 'autoresponder', cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<Mailbox>>(`/mailboxes?${params.toString()}`);
    },
  });

  const columns: Array<DataTableColumn<Mailbox>> = [
    {
      id: 'address',
      header: 'メールアドレス',
      cell: (row) => (
        <AppLink to={`/mailboxes/${row.id}`} className="font-medium hover:underline">
          {row.address}
        </AppLink>
      ),
    },
    {
      id: 'state',
      header: '自動応答',
      width: 'w-32',
      cell: (row) => (
        <Badge variant={row.autoresponderEnabled ? 'info' : 'muted'} size="sm">
          {row.autoresponderEnabled ? '有効' : '無効'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-32',
      align: 'right',
      cell: (row) => (
        <Button asChild variant="outline" size="sm">
          <AppLink to={`/mailboxes/${row.id}`}>設定する</AppLink>
        </Button>
      ),
    },
  ];

  const items = list.data?.items ?? [];
  const activeCount = items.filter((m) => m.autoresponderEnabled).length;

  return (
    <>
      <PageHeader
        title="自動応答"
        description="不在時などに、届いたメールへ自動で返信する設定です。"
      />

      <div className="space-y-4">
        <HelpNote title="自動応答を使うときの注意">
          迷惑メールにも返信してしまうと、こちらのアドレスが「使われている」と知られる原因になります。
          本サービスでは迷惑メールと判定されたメールには自動応答しません。
          また、同じ相手には 1 日 1 回だけ返信する設定を推奨しています。
        </HelpNote>

        {activeCount > 0 ? (
          <p className="text-sm text-muted-foreground">
            現在 {activeCount} 件のメールアドレスで自動応答が有効です。
          </p>
        ) : null}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.isError ? '一覧を取得できませんでした。' : undefined}
          empty={
            <EmptyState
              icon={Reply}
              title="メールアドレスがありません"
              description="自動応答はメールアドレスごとに設定します。先にメールアドレスを作成してください。"
              action={
                <Button asChild>
                  <AppLink to="/mailboxes">メールアドレスを作成する</AppLink>
                </Button>
              }
            />
          }
          footer={
            <CursorPagination
              nextCursor={list.data?.nextCursor ?? null}
              canGoPrev={page.canGoPrev}
              onNext={() => page.goNext(list.data?.nextCursor ?? null)}
              onPrev={page.goPrev}
              limit={page.limit}
              onLimitChange={page.setLimit}
              currentCount={items.length}
              loading={list.isFetching}
            />
          }
        />
      </div>
    </>
  );
}
